/*
 * Copyright (c) 2026 Joseph Duggan. All rights reserved.
 */

import { t } from '@sidekick/copy';
import { appPalette } from '@sidekick/design-tokens';
import { useState, type FormEvent } from 'react';
import { PageHeader } from '../components/PageHeader.js';
import { inputStyle, monoLabel, today } from '../lib/ui.js';
import { trpc } from '../lib/trpc.js';

const p = appPalette;

/**
 * The Record — the durable account of what you've finished.
 *
 * A completed feed (auto-populated as actions are marked done) sits above
 * a curated list of wins. Starring a completed action promotes it into the
 * wins list, where it can carry impact and audience for later reuse.
 */
export function RecordPage() {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', minHeight: '100%' }}>
      <PageHeader label={t.nav.record} />

      <div style={{ flex: 1, display: 'flex', justifyContent: 'center', padding: '40px 22px' }}>
        <div
          style={{
            width: '100%',
            maxWidth: 620,
            display: 'flex',
            flexDirection: 'column',
            gap: 40,
          }}
        >
          <Completed />
          <Wins />
        </div>
      </div>
    </div>
  );
}

const serifBody: React.CSSProperties = {
  fontFamily: 'var(--font-serif)',
  fontFeatureSettings: 'var(--serif-feat)',
  fontSize: 15,
  color: p.fg,
};

const emptyLine: React.CSSProperties = {
  fontFamily: 'var(--font-serif)',
  fontFeatureSettings: 'var(--serif-feat)',
  fontStyle: 'italic',
  fontSize: 15,
  color: p.muted,
  margin: 0,
};

function Completed() {
  const utils = trpc.useUtils();
  const completed = trpc.record.completed.useQuery();
  const addWin = trpc.record.addWin.useMutation({
    onSuccess: async () => {
      await utils.record.wins.invalidate();
    },
  });

  // Transient per-action confirmation: the API gives us no "already
  // starred" flag, so a filled ★ on the just-starred row is the signal.
  const [starred, setStarred] = useState<ReadonlySet<string>>(new Set());

  const onStar = (action: { id: string; description: string; completedAt: string | null }) => {
    if (starred.has(action.id)) return;
    addWin.mutate(
      {
        occurredOn: dateOnly(action.completedAt) ?? today(),
        what: action.description,
        linkedActionId: action.id,
      },
      {
        onSuccess: () => setStarred((prev) => new Set(prev).add(action.id)),
      },
    );
  };

  const rows = completed.data ?? [];

  return (
    <section style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      <div style={monoLabel}>Completed</div>

      {completed.isPending ? (
        <span style={{ ...monoLabel, marginTop: 8 }}>…</span>
      ) : rows.length === 0 ? (
        <p style={emptyLine}>Nothing completed yet.</p>
      ) : (
        <div>
          {rows.map((a) => (
            <div
              key={a.id}
              style={{
                display: 'grid',
                gridTemplateColumns: '1fr auto auto',
                alignItems: 'baseline',
                gap: 12,
                padding: '12px 0',
                borderTop: `1px solid ${p.line}`,
              }}
            >
              <span style={serifBody}>{a.description}</span>
              <span style={{ ...monoLabel, color: p.muted }}>{formatDate(a.completedAt)}</span>
              <button
                type="button"
                onClick={() => onStar(a)}
                disabled={addWin.isPending || starred.has(a.id)}
                title="Star as a win"
                aria-label="Star as a win"
                style={{
                  background: 'transparent',
                  border: 'none',
                  color: starred.has(a.id) ? p.accent : p.muted,
                  cursor: starred.has(a.id) ? 'default' : addWin.isPending ? 'wait' : 'pointer',
                  fontSize: 16,
                  padding: '0 4px',
                }}
              >
                {starred.has(a.id) ? '★' : '☆'}
              </button>
            </div>
          ))}
        </div>
      )}
      {addWin.error ? (
        <span style={{ ...monoLabel, color: p.accent }}>{addWin.error.message}</span>
      ) : null}
    </section>
  );
}

function Wins() {
  const utils = trpc.useUtils();
  const wins = trpc.record.wins.useQuery();
  const addWin = trpc.record.addWin.useMutation({
    onSuccess: async () => {
      await utils.record.wins.invalidate();
    },
  });
  const removeWin = trpc.record.removeWin.useMutation({
    onSuccess: async () => {
      await utils.record.wins.invalidate();
    },
  });

  const [what, setWhat] = useState('');
  const [impact, setImpact] = useState('');
  const [audience, setAudience] = useState('');
  const [occurredOn, setOccurredOn] = useState(today());

  const canSubmit = what.trim() !== '' && !addWin.isPending;

  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    if (!canSubmit) return;
    addWin.mutate(
      {
        occurredOn: occurredOn || today(),
        what: what.trim(),
        impact: impact.trim() || null,
        audience: audience.trim() || null,
      },
      {
        onSuccess: () => {
          setWhat('');
          setImpact('');
          setAudience('');
          setOccurredOn(today());
        },
      },
    );
  };

  const rows = wins.data ?? [];

  return (
    <section style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      <div style={monoLabel}>Wins</div>

      {wins.isPending ? (
        <span style={{ ...monoLabel, marginTop: 8 }}>…</span>
      ) : rows.length === 0 ? (
        <p style={emptyLine}>No wins recorded yet.</p>
      ) : (
        <div>
          {rows.map((w) => (
            <div
              key={w.id}
              style={{
                display: 'grid',
                gridTemplateColumns: '1fr auto auto',
                alignItems: 'baseline',
                gap: 12,
                padding: '12px 0',
                borderTop: `1px solid ${p.line}`,
              }}
            >
              <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                <span style={serifBody}>{w.what}</span>
                {w.impact ? (
                  <span style={{ ...serifBody, fontSize: 14, color: p.muted }}>{w.impact}</span>
                ) : null}
                {w.audience ? (
                  <span style={{ ...monoLabel, color: p.muted }}>{w.audience}</span>
                ) : null}
              </div>
              <span style={{ ...monoLabel, color: p.muted }}>{formatDate(w.occurredOn)}</span>
              <button
                type="button"
                onClick={() => removeWin.mutate({ bragId: w.id })}
                disabled={removeWin.isPending}
                title="Remove win"
                aria-label="Remove win"
                style={{
                  background: 'transparent',
                  border: 'none',
                  color: p.muted,
                  cursor: removeWin.isPending ? 'wait' : 'pointer',
                  fontSize: 16,
                  padding: '0 4px',
                }}
              >
                ×
              </button>
            </div>
          ))}
        </div>
      )}

      <form onSubmit={onSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 8 }}>
        <input
          type="text"
          value={what}
          onChange={(e) => setWhat(e.target.value)}
          placeholder="What you pulled off"
          aria-label="Win"
          style={inputStyle}
        />
        <div style={{ display: 'flex', gap: 10 }}>
          <input
            type="text"
            value={impact}
            onChange={(e) => setImpact(e.target.value)}
            placeholder="Impact (optional)"
            aria-label="Impact"
            style={{ ...inputStyle, flex: 1 }}
          />
          <input
            type="text"
            value={audience}
            onChange={(e) => setAudience(e.target.value)}
            placeholder="Audience (optional)"
            aria-label="Audience"
            style={{ ...inputStyle, flex: 1 }}
          />
        </div>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
          <input
            type="date"
            value={occurredOn}
            onChange={(e) => setOccurredOn(e.target.value)}
            aria-label="Date"
            style={{ ...inputStyle, width: 170 }}
          />
          <button
            type="submit"
            disabled={!canSubmit}
            style={{
              padding: '10px 18px',
              background: p.fg,
              color: p.bg,
              border: 'none',
              fontSize: 13,
              cursor: !canSubmit ? 'not-allowed' : 'pointer',
              opacity: !canSubmit ? 0.6 : 1,
            }}
          >
            {addWin.isPending ? 'Adding…' : 'Add →'}
          </button>
        </div>
      </form>
      {addWin.error ? (
        <span style={{ ...monoLabel, color: p.accent }}>{addWin.error.message}</span>
      ) : null}
      {removeWin.error ? (
        <span style={{ ...monoLabel, color: p.accent }}>{removeWin.error.message}</span>
      ) : null}
    </section>
  );
}

/** Pull the YYYY-MM-DD prefix off an ISO/date string, or null if absent. */
function dateOnly(value: string | null): string | null {
  if (!value) return null;
  const match = /^(\d{4}-\d{2}-\d{2})/.exec(value);
  return match?.[1] ?? null;
}

/** Format a date string for display, matching the mono date treatment. */
function formatDate(value: string | null): string {
  if (!value) return '—';
  const d = new Date(value.length === 10 ? `${value}T00:00:00` : value);
  if (Number.isNaN(d.getTime())) return value;
  return d.toLocaleDateString(undefined, { day: 'numeric', month: 'short' });
}
